<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="self-order-qr-code">
    <!-- セルフオーダー状態表示・操作ボタン -->
    <v-card
      outlined
      class="mb-2"
    >
      <v-card-text class="d-flex align-center justify-space-between py-1 px-3">
        <div class="d-flex align-center">
          <v-icon
            :color="isSelfOrderOpen ? 'success' : 'grey'"
            small
          >
            {{ icons.mdiQrcode }}
          </v-icon>
          <v-switch
            :input-value="isSelfOrderOpen"
            :loading="isToggling"
            :disabled="isToggling"
            hide-details
            dense
            class="mt-0 pt-0 ml-1"
            @change="toggleSelfOrder"
          />
        </div>

        <div
          v-if="isSelfOrderOpen"
          class="d-flex align-center"
        >
          <v-btn
            x-small
            color="primary"
            class="mr-1"
            :loading="isLoadingQr"
            @click="showQrCodeDialog"
          >
            <v-icon
              left
              x-small
            >
              {{ icons.mdiQrcode }}
            </v-icon>
            QR
          </v-btn>

          <v-btn
            x-small
            outlined
            color="primary"
            class="mr-1"
            :loading="isCopyingUrl"
            @click="copyUrl"
          >
            <v-icon
              left
              x-small
            >
              {{ icons.mdiContentCopy }}
            </v-icon>
            URL
          </v-btn>

          <v-btn
            x-small
            outlined
            :color="isReceiptPrinterConnected ? 'primary' : 'grey'"
            class="mr-1"
            :loading="isPrinting"
            :disabled="!isReceiptPrinterConnected"
            @click="printQrCode"
          >
            <v-icon x-small>
              {{ icons.mdiPrinter }}
            </v-icon>
          </v-btn>

          <v-btn
            x-small
            outlined
            color="primary"
            :loading="isLoadingPreview"
            @click="showPreviewDialog"
          >
            <v-icon x-small>
              {{ icons.mdiEye }}
            </v-icon>
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- QRコード表示ダイアログ -->
    <v-dialog
      v-model="isQrDialogOpen"
      max-width="400"
      persistent
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>セルフオーダー QRコード</span>
          <v-btn
            icon
            @click="closeQrDialog"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text-center">
          <!-- テーブル情報 -->
          <div class="mb-4">
            <div class="text-h6">
              {{ clubName }}
            </div>
            <div class="text-subtitle-1">
              {{ hallNameDisplay }} - {{ tableDisplayName }}
            </div>
          </div>

          <!-- QRコード -->
          <div
            ref="qrContainer"
            class="qr-code-container"
          >
            <canvas
              v-if="qrCodeUrl"
              id="self-order-qrcode"
              class="qr-canvas"
            />
            <v-progress-circular
              v-else
              indeterminate
              color="primary"
            />
          </div>

          <!-- セキュリティレベル表示 -->
          <v-chip
            v-if="securityLevel"
            small
            :color="securityLevelColor"
            class="mt-3 mb-2"
          >
            {{ securityLevelLabel }}
          </v-chip>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 印刷プレビューダイアログ -->
    <v-dialog
      v-model="isPreviewDialogOpen"
      max-width="400"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>印刷プレビュー</span>
          <v-btn
            icon
            @click="isPreviewDialogOpen = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text-center">
          <div
            v-if="previewSvg"
            class="preview-svg-container"
            v-html="previewSvg"
          />
          <v-progress-circular
            v-else
            indeterminate
            color="primary"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, nextTick, getCurrentInstance, inject,
} from '@vue/composition-api'
import {
  mdiQrcode,
  mdiClose,
  mdiContentCopy,
  mdiPrinter,
  mdiEye,
} from '@mdi/js'
import { format, parseISO } from 'date-fns'
import QrCode from 'qrcode'
import { useQrCodeSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import TableApi from '@/api/waiter/Table'
import useCurrentData from '@/views/composable/useCurrentData'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'

export default {
  name: 'SelfOrderQrCode',
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => value === null || !Number.isNaN(Number(value)),
      default: null,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '',
    },
    selfOrderStatus: {
      type: String,
      default: null,
    },
    hallName: {
      type: String,
      default: '',
    },
    startAt: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { currentClub } = useCurrentData()
    const { printer, checkConnection } = useReceiptPrinterConnection()
    const isReceiptPrinterConnected = inject('isReceiptPrinterConnected', ref(false))

    // State
    const isQrDialogOpen = ref(false)
    const isPreviewDialogOpen = ref(false)
    const isLoadingQr = ref(false)
    const isCopyingUrl = ref(false)
    const isToggling = ref(false)
    const isPrinting = ref(false)
    const isLoadingPreview = ref(false)
    const qrCodeUrl = ref(null)
    const securityLevel = ref(null)
    const localSelfOrderStatus = ref(props.selfOrderStatus)
    const previewSvg = ref(null)

    // Computed
    const isSelfOrderOpen = computed(() => localSelfOrderStatus.value === 'open')

    const clubName = computed(() => currentClub.value?.name || '')
    const hallNameDisplay = computed(() => props.hallName || '')

    const tableDisplayName = computed(() => {
      const prefix = props.tablePrefix || ''
      const suffix = props.tableSuffix || ''

      return `${prefix}${props.tableNumber}${suffix}`
    })

    const securityLevelLabel = computed(() => {
      const labels = {
        staff_only: 'スタッフ専用',
        standard: '標準',
        secure: 'セキュア（動的QR）',
      }

      return labels[securityLevel.value] || ''
    })

    const securityLevelColor = computed(() => {
      const colors = {
        staff_only: 'info',
        standard: 'warning',
        secure: 'success',
      }

      return colors[securityLevel.value] || 'grey'
    })

    // QR code slip print composable options
    const formattedStartAt = computed(() => {
      if (!props.startAt) return ''
      try {
        return format(parseISO(props.startAt), 'yyyy/MM/dd HH:mm')
      } catch {
        return props.startAt
      }
    })

    const slipPrintOptions = computed(() => ({
      ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
      printer: printer.value,
      qrCodeUrl: qrCodeUrl.value || '',
      hallName: props.hallName || '',
      tableName: tableDisplayName.value,
      startAt: formattedStartAt.value,
    }))

    const { send: sendSlipPrint, svg: slipPrintSvg, onReceive: onReceiveSlipPrint } = useQrCodeSlipPrint(slipPrintOptions)

    onReceiveSlipPrint.value = response => {
      if (!response.success) {
        vm.$toast.error('QRコードの印刷に失敗しました')
      }
    }

    // Methods
    const fetchQrCodeUrl = async () => {
      const response = await TableApi.getSelfOrderQr(props.tableId)
      const data = response.data.data || response.data

      qrCodeUrl.value = data.url
      securityLevel.value = data.security_level

      return data
    }

    const printQrCodeInternal = async () => {
      await fetchQrCodeUrl()
      await checkConnection()
      await sendSlipPrint()
    }

    const toggleSelfOrder = async value => {
      isToggling.value = true
      try {
        if (value) {
          await TableApi.selfOrderOpen(props.tableId)
          localSelfOrderStatus.value = 'open'
          emit('updated:self-order-status', localSelfOrderStatus.value)
        } else {
          await TableApi.selfOrderClose(props.tableId)
          localSelfOrderStatus.value = 'closed'
          emit('updated:self-order-status', localSelfOrderStatus.value)
        }
      } catch (error) {
        console.error('セルフオーダー切り替えエラー:', error)
      } finally {
        isToggling.value = false
      }
    }

    const generateQrCode = canvasId => {
      const canvas = document.getElementById(canvasId)
      if (!canvas || !qrCodeUrl.value) return

      QrCode.toCanvas(canvas, qrCodeUrl.value, {
        width: 200,
        margin: 2,
      }, error => {
        if (error) console.error('QRコード生成エラー:', error)
      })
    }

    const showQrCodeDialog = async () => {
      isLoadingQr.value = true
      isQrDialogOpen.value = true

      try {
        await fetchQrCodeUrl()

        await nextTick()
        generateQrCode('self-order-qrcode')
      } catch (error) {
        console.error('QRコード取得エラー:', error)
        isQrDialogOpen.value = false
      } finally {
        isLoadingQr.value = false
      }
    }

    const closeQrDialog = () => {
      isQrDialogOpen.value = false
      qrCodeUrl.value = null
    }

    const copyUrl = async () => {
      isCopyingUrl.value = true
      try {
        const data = await fetchQrCodeUrl()

        await navigator.clipboard.writeText(data.url)
        vm.$toast.success('URLをコピーしました')
      } catch (error) {
        console.error('URLコピーエラー:', error)
        vm.$toast.error('URLのコピーに失敗しました')
      } finally {
        isCopyingUrl.value = false
      }
    }

    const printQrCode = async () => {
      isPrinting.value = true
      try {
        await printQrCodeInternal()
        vm.$toast.success('QRコードを印刷しました')
      } catch (error) {
        console.error('QRコード印刷エラー:', error)
        vm.$toast.error('QRコードの印刷に失敗しました')
      } finally {
        isPrinting.value = false
      }
    }

    const showPreviewDialog = async () => {
      isLoadingPreview.value = true
      try {
        await fetchQrCodeUrl()

        await nextTick()
        previewSvg.value = slipPrintSvg.value
        isPreviewDialogOpen.value = true
      } catch (error) {
        console.error('プレビュー取得エラー:', error)
        vm.$toast.error('プレビューの取得に失敗しました')
      } finally {
        isLoadingPreview.value = false
      }
    }

    return {
      // State
      isQrDialogOpen,
      isPreviewDialogOpen,
      isLoadingQr,
      isCopyingUrl,
      isToggling,
      isPrinting,
      isLoadingPreview,
      isReceiptPrinterConnected,
      qrCodeUrl,
      securityLevel,
      previewSvg,

      // Computed
      isSelfOrderOpen,
      clubName,
      hallNameDisplay,
      tableDisplayName,
      securityLevelLabel,
      securityLevelColor,

      // Methods
      toggleSelfOrder,
      showQrCodeDialog,
      closeQrDialog,
      copyUrl,
      printQrCode,
      showPreviewDialog,

      // Icons
      icons: {
        mdiQrcode,
        mdiClose,
        mdiContentCopy,
        mdiPrinter,
        mdiEye,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.self-order-qr-code {
  .qr-code-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  .qr-canvas {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}
</style>

<style lang="scss">
.preview-svg-container {
  display: flex;
  justify-content: center;
  padding: 16px 0;
  background-color: #fff;
  border-radius: 8px;

  svg {
    max-width: 280px;
    height: auto;
  }
}
</style>
